/**
 * HostSearchInput - generic "find a host" combobox.
 *
 * Single search box with a live results dropdown. Searches address,
 * ZIP, owner name, and vehicle identifiers (VIN / hull-id /
 * registration number) via ``GET /api/hosts/search/``. Tenant scope
 * is enforced server-side.
 *
 * Used by the transfer wizard's step 1 and intended for reuse by the
 * claim wizard, ad-hoc admin lookups, etc. Drop-in replacement for
 * the older ZipFirstSearch component.
 */

import { useEffect, useMemo, useRef, useState } from 'react'

import { Input } from '@/shared/components/ui/input'
import { Button } from '@/shared/components/ui/button'
import {
  useHostSearch,
  type HostSearchFieldGroup,
  type HostSearchResult,
} from '@/admin/hooks/use-host-search'
import { Loader2, MapPin, Search, User } from 'lucide-react'

interface HostSearchInputProps {
  onSelect: (result: HostSearchResult) => void
  /** Optional narrowing for callers that only want some field groups. */
  fields?: HostSearchFieldGroup[]
  /** Override default placeholder copy. */
  placeholder?: string
  /** Override default heading copy. */
  heading?: string
  /**
   * Drop the heading and its sub-line, for a host that already titles the
   * search (e.g. a dialog), so the title is not said twice.
   */
  hideHeading?: boolean
}

const DEBOUNCE_MS = 200

/** Wrap matching substrings in <mark> for visible highlighting. */
function highlight(text: string, query: string): React.ReactNode {
  if (!query) return text
  const lower = text.toLowerCase()
  const q = query.toLowerCase()
  const out: React.ReactNode[] = []
  let i = 0
  while (i < text.length) {
    const j = lower.indexOf(q, i)
    if (j === -1) {
      out.push(text.slice(i))
      break
    }
    if (j > i) out.push(text.slice(i, j))
    out.push(
      <mark
        key={`${i}-${j}`}
        className="bg-[var(--gold-bg)] text-[var(--ink)]"
      >
        {text.slice(j, j + q.length)}
      </mark>,
    )
    i = j + q.length
  }
  return out
}

export function HostSearchInput({
  onSelect,
  fields,
  placeholder = 'Search address, ZIP, owner name, or registration #',
  heading = 'Find a property',
  hideHeading = false,
}: HostSearchInputProps) {
  const [raw, setRaw] = useState('')
  const [query, setQuery] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  // Debounce the live query so each keystroke isn't a request.
  useEffect(() => {
    const handle = setTimeout(() => setQuery(raw), DEBOUNCE_MS)
    return () => clearTimeout(handle)
  }, [raw])

  const { data, isFetching, isError } = useHostSearch(query, { fields })
  const results = data?.results ?? []
  const showDropdown = query.trim().length >= 2

  const helperText = useMemo(() => {
    if (raw.trim().length === 0) {
      return 'Searches address, ZIP, owner and registration number.'
    }
    if (raw.trim().length < 2) {
      return 'Keep typing - at least 2 characters needed to search.'
    }
    return null
  }, [raw])

  return (
    <div className="max-w-[760px]">
      {!hideHeading && (
        <>
          <h2 className="mb-1 font-heading text-[20px] text-[var(--ink)]">
            {heading}
          </h2>
          <p className="mb-5 text-[13px] text-[var(--ink-3)]">
            {placeholder}.
          </p>
        </>
      )}

      <div className="relative">
        <div className="flex items-center gap-2 rounded-[8px] border-[0.5px] border-[var(--rule-strong)] bg-[var(--bg)] px-3 py-2.5 focus-within:border-[var(--ink-3)]">
          <Search
            className="h-4 w-4 shrink-0 text-[var(--ink-3)]"
            aria-hidden="true"
          />
          <Input
            ref={inputRef}
            value={raw}
            onChange={(e) => setRaw(e.target.value)}
            placeholder={placeholder}
            className="h-auto flex-1 border-0 bg-transparent p-0 text-[14px] focus-visible:ring-0"
            data-testid="host-search-input"
            autoFocus
          />
          {isFetching && showDropdown && (
            <Loader2
              className="h-3.5 w-3.5 shrink-0 animate-spin text-[var(--ink-3)]"
              aria-label="Loading"
            />
          )}
          {raw && (
            <Button
              type="button"
              variant="ghost"
              size="xs"
              onClick={() => {
                setRaw('')
                setQuery('')
                inputRef.current?.focus()
              }}
              className="h-auto px-2 py-0.5 text-[11px] text-[var(--ink-3)]"
            >
              Clear
            </Button>
          )}
        </div>

        {helperText && (
          <p className="mt-2 text-[11.5px] text-[var(--ink-3)]">
            {helperText}
          </p>
        )}

        {showDropdown && (
          <div
            className="mt-2 overflow-hidden rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)]"
            data-testid="host-search-results"
          >
            {isError && (
              <p className="px-4 py-3 text-[12px] text-[var(--status-warn)]">
                Search failed. Try a different query.
              </p>
            )}
            {!isError && !isFetching && results.length === 0 && (
              <p className="px-4 py-3 text-[12px] text-[var(--ink-3)]">
                No properties match &ldquo;{query}&rdquo;.
              </p>
            )}
            {results.map((row, i) => (
              <button
                key={row.uuid}
                type="button"
                onClick={() => onSelect(row)}
                className={`flex w-full flex-col items-start gap-1 px-4 py-3 text-left transition-colors hover:bg-[var(--bg-subtle)] ${
                  i === results.length - 1
                    ? ''
                    : 'border-b-[0.5px] border-b-[var(--rule)]'
                }`}
                data-testid="host-search-result"
              >
                <div className="flex w-full items-baseline justify-between gap-3">
                  <span className="flex items-center gap-1.5 text-[13.5px] font-medium text-[var(--ink)]">
                    <MapPin
                      className="h-3.5 w-3.5 shrink-0 text-[var(--ink-3)]"
                      aria-hidden="true"
                    />
                    {highlight(
                      row.display_name ||
                        row.address?.value.address_line_1 ||
                        '(unnamed)',
                      query,
                    )}
                  </span>
                  <span className="shrink-0 text-[10px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
                    {row.active_holding_count}{' '}
                    {row.active_holding_count === 1
                      ? 'registration'
                      : 'registrations'}
                  </span>
                </div>
                <div className="flex flex-wrap items-center gap-x-2 text-[11.5px] text-[var(--ink-3)]">
                  {(row.address?.value.locality ||
                    row.address?.value.postal_code) && (
                    <span>
                      {row.address?.value.locality
                        ? highlight(row.address.value.locality, query)
                        : ''}
                      {row.address?.value.locality &&
                      row.address?.value.postal_code
                        ? ', '
                        : ''}
                      {row.address?.value.postal_code
                        ? highlight(row.address.value.postal_code, query)
                        : ''}
                    </span>
                  )}
                  {row.current_owner_name && (
                    <span className="flex items-center gap-1">
                      <User className="h-3 w-3" aria-hidden="true" />
                      {highlight(row.current_owner_name, query)}
                    </span>
                  )}
                  {row.vehicle_id && (
                    <span className="font-mono">
                      {highlight(row.vehicle_id, query)}
                    </span>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
